<script lang="ts">
    import {onMount} from 'svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';
    import {Eye, EyeSlash, Info} from 'phosphor-svelte';
    import RequiredBadge from '$lib/components/common/badges/RequiredBadge.svelte';

    export let clientId: string = '';
  export let entityId: string = '';
  export let clientName: string = '';
  export let organisationId: string | undefined;

  const DEFAULT_REMOTE_URL =
    'https://uk1.accountsiq.com/system/dashboard/integration/integration_2_0.asmx';

  type Entity = {
    id: number;
    name: string;
    code: string;
    hasAccountsIQConfig: boolean;
  };

  let entities: Entity[] = [];
  let selectedEntity: Entity | null = null;
  let newEntityName: string = '';
  let isLoadingEntities = false;
  let showConfigSection = false;

  // AccountsIQ credentials
  let accountsiqClientId = '';
  let accountsiqClientSecret = '';
  let accountsiqEntityId = '';
  let accountsiqRemoteUrl = DEFAULT_REMOTE_URL;

  // Financial settings
  let timeZoneId = 'Australia/Brisbane';
  let budgetType = 1;
  let financialYearEndMonth = 8;

  const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  let isLoading = false;
  let isExecuting = false;
  let hasExistingConfig = false;
  let showSecret = false;
  let urlError = '';

  function handleApiError(error: unknown, message: string) {
    if (error instanceof Error) {
      console.error(`${message}:`, error.message);
      showToast({ title: 'Error', message: error.message, type: 'error' });
    } else {
      console.error(`${message}:`, error);
      showToast({ title: 'Error', message: message, type: 'error' });
    }
  }

  function validateRemoteUrl(url: string): boolean {
    if (!url) { urlError = ''; return false; }
    if (!url.startsWith('https://')) { urlError = 'URL must start with https://'; return false; }
    urlError = '';
    return true;
  }

  function isValidGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }

  $: isUrlValid = validateRemoteUrl(accountsiqRemoteUrl);
  $: isClientIdValid = isValidGuid(accountsiqClientId);
  $: secretIsHidden = accountsiqClientSecret === '••••••';
  $: formIsValid = !!(
    clientId &&
    isClientIdValid &&
    accountsiqClientSecret &&
    accountsiqEntityId &&
    accountsiqRemoteUrl &&
    isUrlValid
  );
  $: canSave = formIsValid && !isExecuting;
  $: showConfigSection = !!(selectedEntity || newEntityName);

  onMount(async () => {
    if (clientId) await loadInitialEntities();
  });

  async function loadInitialEntities() {
    if (!clientId || isLoadingEntities) return;
    isLoadingEntities = true;
    try {
      const response = await fetchWithAuth(`/api/accountsiq/entities/${clientId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          entities = result.entities;
          if (entityId) {
            const existing = entities.find((e) => e.id.toString() === entityId);
            if (existing) {
              selectedEntity = existing;
              await loadExistingConfig();
            } else {
              entityId = '';
            }
          }
        } else {
          showToast({ title: 'Error', message: result.error || 'Failed to load entities', type: 'error' });
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to load entities');
    } finally {
      isLoadingEntities = false;
    }
  }

  async function loadExistingConfig() {
    if (!clientId || !entityId || isLoading) return;
    isLoading = true;
    hasExistingConfig = false;
    try {
      const response = await fetchWithAuth(
        `/api/accountsiq/config/${clientId}/${entityId || 0}`,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.exists) {
          hasExistingConfig = true;
          const cfg = result.data.config;
          accountsiqClientId = cfg.accountsiqClientId ?? '';
          accountsiqClientSecret = cfg.accountsiqClientSecret ?? '';
          accountsiqEntityId = cfg.accountsiqEntityId ?? '';
          accountsiqRemoteUrl = cfg.accountsiqRemoteUrl || DEFAULT_REMOTE_URL;
          timeZoneId = cfg.timeZoneId || 'Australia/Brisbane';
          budgetType = cfg.budgetType ?? 1;
          financialYearEndMonth = cfg.financialYearEndMonth ?? 8;
          showToast({ title: 'Existing Configuration Loaded', message: 'This entity has existing AccountsIQ configuration.', type: 'info' });
        }
      }
    } catch (error) {
      console.error('Failed to load AccountsIQ config:', error);
    } finally {
      isLoading = false;
    }
  }

  async function saveConfiguration() {
    if (!canSave) return;
    isExecuting = true;
    try {
      const body = {
        clientId: parseInt(clientId),
        entityId: entityId ? parseInt(entityId) : 0,
        entityName: newEntityName || selectedEntity?.name || clientName,
        accountsIQClientId: accountsiqClientId,
        accountsIQClientSecret: accountsiqClientSecret,
        accountsIQEntityId: accountsiqEntityId,
        accountsIQRemoteUrl: accountsiqRemoteUrl,
        timeZoneId,
        budgetType,
        financialYearEndMonth,
      };
      const response = await fetchWithAuth('/api/accountsiq/setup', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (result.success) {
        const newEntityId = result.data?.entityId;
        showToast({ title: 'Success', message: 'AccountsIQ configuration saved successfully', type: 'success' });
        if (!entityId && newEntityId) {
          entityId = newEntityId.toString();
          hasExistingConfig = true;
          await loadEntitiesWithoutResetting();
          const created = entities.find((e) => e.id === newEntityId);
          if (created) { newEntityName = ''; selectedEntity = created; }
        } else {
          await loadEntitiesWithoutResetting();
        }
      } else {
        showToast({ title: 'Error', message: 'Failed to save AccountsIQ configuration', type: 'error' });
      }
    } catch (error) {
      handleApiError(error, 'Failed to save AccountsIQ configuration');
    } finally {
      isExecuting = false;
    }
  }

  function resetForm() {
    accountsiqClientId = '';
    accountsiqClientSecret = '';
    accountsiqEntityId = '';
    accountsiqRemoteUrl = DEFAULT_REMOTE_URL;
    timeZoneId = 'Australia/Brisbane';
    budgetType = 1;
    financialYearEndMonth = 8;
    hasExistingConfig = false;
    showSecret = false;
  }

  function resetAll() {
    resetForm();
    selectedEntity = null;
    newEntityName = '';
    entityId = '';
  }

  async function loadEntitiesWithoutResetting() {
    if (!clientId || isLoadingEntities) return;
    isLoadingEntities = true;
    try {
      const response = await fetchWithAuth(`/api/accountsiq/entities/${clientId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          const currentEntityId = selectedEntity?.id;
          entities = result.entities;
          if (currentEntityId) {
            const updated = entities.find((e) => e.id === currentEntityId);
            if (updated) selectedEntity = updated;
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh entities:', error);
    } finally {
      isLoadingEntities = false;
    }
  }

  async function loadEntities() {
    if (!clientId || isLoadingEntities) return;
    isLoadingEntities = true;
    selectedEntity = null;
    newEntityName = '';
    entityId = '';
    resetForm();
    try {
      const response = await fetchWithAuth(`/api/accountsiq/entities/${clientId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          entities = result.entities;
          showToast({ title: 'Success', message: 'Entities refreshed successfully', type: 'success' });
        } else {
          showToast({ title: 'Error', message: result.error || 'Failed to load entities', type: 'error' });
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to load entities');
    } finally {
      isLoadingEntities = false;
    }
  }

  function handleEntitySelection() {
    if (selectedEntity) {
      newEntityName = '';
      entityId = selectedEntity.id.toString();
      if (selectedEntity.hasAccountsIQConfig) {
        loadExistingConfig();
      } else {
        resetForm();
      }
    } else {
      entityId = '';
    }
  }

  function handleNewEntityInput() {
    if (newEntityName && (selectedEntity !== null || hasExistingConfig)) {
      selectedEntity = null;
      entityId = '';
      resetForm();
    }
  }
</script>

<div class="p-5">
  <h3 class="mb-4 text-xl font-semibold">AccountsIQ Integration Setup</h3>
  <p class="mb-4 text-sm text-surface-700">
    Configure AccountsIQ integration for this client using OAuth credentials and company settings.
  </p>

  <!-- Entity Selection -->
  <div class="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
    <h4 class="mb-4 font-semibold">Select or Create Entity</h4>
    <p class="mb-4 text-sm text-gray-600">Choose an existing entity or create a new one for AccountsIQ integration.</p>

    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div>
        <label for="entity-select" class="mb-2 block text-sm font-medium text-gray-700">
          Existing Entity
        </label>
        <div class="flex items-end gap-3">
          <select
            id="entity-select"
            bind:value={selectedEntity}
            on:change={handleEntitySelection}
            class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
            disabled={isLoadingEntities || newEntityName !== '' || isExecuting}
          >
            <option value={null}>-- Select Entity --</option>
            {#each entities as entity}
              <option value={entity}>
                {entity.name}{entity.hasAccountsIQConfig ? ' (has AccountsIQ config)' : ''}
              </option>
            {/each}
          </select>
          <Button
            label={isLoadingEntities ? 'Loading...' : 'Refresh'}
            onClick={loadEntities}
            disabled={isLoadingEntities || isExecuting}
            colour="secondary"
            size="small"
          />
        </div>
        {#if isLoadingEntities}
          <p class="mt-2 text-sm text-gray-500">Loading entities...</p>
        {/if}
      </div>

      <div>
        <label for="new-entity-name" class="mb-2 block text-sm font-medium text-gray-700">
          New Entity Name
        </label>
        <input
          id="new-entity-name"
          type="text"
          bind:value={newEntityName}
          on:input={handleNewEntityInput}
          placeholder="Enter new entity name"
          class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
          disabled={selectedEntity !== null || isExecuting}
        />
        <p class="mt-2 text-xs text-gray-500">Leave blank to select an existing entity</p>
      </div>
    </div>
  </div>

  <!-- AccountsIQ Configuration -->
  {#if showConfigSection}
    <div class="space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div class="flex items-center justify-between">
        <h4 class="font-semibold">AccountsIQ API Configuration</h4>
        {#if isLoading}
          <span class="text-sm text-gray-500">Loading existing configuration...</span>
        {:else if hasExistingConfig}
          <div class="flex items-center gap-2 rounded-md bg-blue-100 px-3 py-1 text-sm text-blue-700">
            <Info size={16} />
            <span>Updating existing configuration</span>
          </div>
        {/if}
      </div>

      {#if selectedEntity}
        <div class="rounded-md bg-blue-50 p-3 text-sm text-blue-700">
          <p>Configuring for entity: <strong>{selectedEntity.name}</strong></p>
        </div>
      {:else if newEntityName}
        <div class="rounded-md bg-green-50 p-3 text-sm text-green-700">
          <p>Creating new entity: <strong>{newEntityName}</strong></p>
        </div>
      {/if}

      <!-- Credentials -->
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label for="aiq-client-id" class="mb-2 flex items-center gap-2 text-sm font-medium">
            AccountsIQ Client ID
            <RequiredBadge />
          </label>
          <input
            id="aiq-client-id"
            type="text"
            bind:value={accountsiqClientId}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
            class:border-gray-300={accountsiqClientId && isClientIdValid}
            class:border-primary-200={!accountsiqClientId}
            class:border-error-500={accountsiqClientId && !isClientIdValid}
            disabled={isExecuting}
          />
          {#if accountsiqClientId && !isClientIdValid}
            <p class="mt-1 text-xs text-error-500">Must be a valid GUID</p>
          {/if}
        </div>

        <div>
          <label for="aiq-client-secret" class="mb-2 flex items-center gap-2 text-sm font-medium">
            AccountsIQ Client Secret
            <RequiredBadge />
          </label>
          {#if secretIsHidden}
            <div class="flex items-center gap-2">
              <div class="flex-1 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-500 italic">
                Secret is set but not visible
              </div>
              <Button
                label="Change"
                onClick={() => { accountsiqClientSecret = ''; showSecret = false; }}
                disabled={isExecuting}
                colour="secondary"
                size="small"
              />
            </div>
          {:else}
            <div class="relative">
              {#if showSecret}
                <input
                  id="aiq-client-secret"
                  type="text"
                  bind:value={accountsiqClientSecret}
                  placeholder="Enter client secret"
                  class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
                  class:border-gray-300={accountsiqClientSecret}
                  class:border-primary-200={!accountsiqClientSecret}
                  disabled={isExecuting}
                />
              {:else}
                <input
                  id="aiq-client-secret"
                  type="password"
                  bind:value={accountsiqClientSecret}
                  placeholder="Enter client secret"
                  class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
                  class:border-gray-300={accountsiqClientSecret}
                  class:border-primary-200={!accountsiqClientSecret}
                  disabled={isExecuting}
                />
              {/if}
              <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                on:click={() => (showSecret = !showSecret)}
                disabled={isExecuting}
              >
                {#if showSecret}<EyeSlash size={20} />{:else}<Eye size={20} />{/if}
              </button>
            </div>
          {/if}
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label for="aiq-entity-id" class="mb-2 flex items-center gap-2 text-sm font-medium">
            AccountsIQ Entity ID
            <RequiredBadge />
          </label>
          <input
            id="aiq-entity-id"
            type="text"
            bind:value={accountsiqEntityId}
            placeholder="Enter AccountsIQ entity ID"
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
            class:border-gray-300={accountsiqEntityId}
            class:border-primary-200={!accountsiqEntityId}
            disabled={isExecuting}
          />
        </div>

        <div>
          <label for="aiq-remote-url" class="mb-2 flex items-center gap-2 text-sm font-medium">
            Remote URL
          </label>
          <input
            id="aiq-remote-url"
            type="url"
            bind:value={accountsiqRemoteUrl}
            placeholder={DEFAULT_REMOTE_URL}
            class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2"
            class:border-gray-300={!accountsiqRemoteUrl || isUrlValid}
            class:border-error-500={accountsiqRemoteUrl && !isUrlValid}
            disabled={isExecuting}
          />
          {#if accountsiqRemoteUrl && urlError}
            <p class="mt-1 text-xs text-error-500">{urlError}</p>
          {/if}
        </div>
      </div>

      <!-- Financial Settings -->
      <div>
        <h5 class="mb-3 text-sm font-semibold text-gray-700">Financial Settings</h5>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label for="time-zone" class="mb-2 block text-sm font-medium text-gray-700">Time Zone</label>
            <input
              id="time-zone"
              type="text"
              bind:value={timeZoneId}
              placeholder="Australia/Brisbane"
              class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isExecuting}
            />
          </div>

          <div>
            <label for="budget-type" class="mb-2 block text-sm font-medium text-gray-700">Budget Type</label>
            <select
              id="budget-type"
              bind:value={budgetType}
              class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isExecuting}
            >
              <option value={1}>Budget</option>
              <option value={2}>Revised Budget</option>
            </select>
          </div>

          <div>
            <label for="fy-end-month" class="mb-2 block text-sm font-medium text-gray-700">Financial Year End Month</label>
            <select
              id="fy-end-month"
              bind:value={financialYearEndMonth}
              class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isExecuting}
            >
              {#each MONTHS as month}
                <option value={month.value}>{month.label}</option>
              {/each}
            </select>
          </div>
        </div>
      </div>
    </div>
  {/if}

  <div class="mt-6 flex justify-end gap-3">
    <Button label="Reset All" onClick={resetAll} disabled={isExecuting} colour="secondary" />
    {#if showConfigSection}
      <Button
        label={isExecuting ? 'Saving...' : hasExistingConfig ? 'Update Configuration' : 'Save Configuration'}
        onClick={saveConfiguration}
        disabled={!canSave}
        colour="primary"
      />
    {/if}
  </div>
</div>
